import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MetaIntegration } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { errorMessage, logError } from '@feathr/hooks';

export interface IUseMetaOAuth {
  initiateOAuth: () => Promise<void>;
  isLoading: boolean;
}

/**
 * Hook to initiate Meta OAuth flow using MobX store.
 *
 * Fetches OAuth config from the backend and redirects to Meta's authorization page.
 */
export function useMetaOAuth(): IUseMetaOAuth {
  const { t } = useTranslation();
  const { MetaIntegrations } = useStore();
  const [isLoading, setIsLoading] = useState(false);

  async function initiateOAuth(): Promise<void> {
    if (isLoading) {
      return;
    }

    setIsLoading(true);

    try {
      const integration: MetaIntegration = await MetaIntegrations.fetchIntegration();
      const oauthUrl = integration.getOAuthUrl();

      if (!oauthUrl) {
        throw new Error(t('OAuth configuration not available'));
      }

      globalThis.location.assign(oauthUrl);
    } catch (err) {
      const errMsg = errorMessage(err, t);
      logError({
        message: 'Failed to initiate Meta OAuth flow',
        level: 'error',
        extra: { error: errMsg },
      });
      toast(errMsg, { type: 'error' });
      setIsLoading(false);
    }
  }

  return { initiateOAuth, isLoading };
}
